import { Component, Suspense, type ErrorInfo, type ReactNode } from "react";
import { Outlet, useLocation } from "react-router";
import { Button } from "../ui/Button";
import { LiveMessage } from "../ui/LiveMessage";
import { recordRouteLoadFailure } from "../../telemetry/routeTracker";

interface RouteErrorBoundaryProps {
  children: ReactNode;
  onError: (error: unknown) => void;
  reloadPage: () => void;
  resetKey: string;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
  error: unknown;
}

class RouteErrorBoundary extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  state: RouteErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: unknown) {
    return { hasError: true, error };
  }

  componentDidCatch(error: unknown, _errorInfo: ErrorInfo) {
    this.props.onError(error);
  }

  componentDidUpdate(prevProps: RouteErrorBoundaryProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-lg border border-red-200 bg-white p-5">
          <LiveMessage tone="alert" className="text-sm text-red-700">
            This page could not load.
          </LiveMessage>
          <p className="mt-2 text-sm text-terroir">
            Reload the page to try loading it again, or use navigation to move elsewhere.
          </p>
          <Button type="button" className="mt-4" onClick={this.props.reloadPage}>
            Reload page
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

export function RouteOutlet({ reloadPage = () => window.location.reload() }: { reloadPage?: () => void }) {
  const location = useLocation();

  return (
    <RouteErrorBoundary
      resetKey={location.pathname}
      reloadPage={reloadPage}
      onError={(error) => recordRouteLoadFailure(location.pathname, error)}
    >
      <Suspense
        fallback={
          <LiveMessage className="rounded-lg bg-white p-5 text-sm text-terroir">
            Loading page...
          </LiveMessage>
        }
      >
        <Outlet />
      </Suspense>
    </RouteErrorBoundary>
  );
}
